import { Link as ChakraLink } from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'

import { DetailSheet } from '../../../components/DetailSheet'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { formatCurrency, formatDateTime, formatIsoDate, useAppToast } from '../../../helpers'
import {
  ISalesReceipt,
  paymentMethodLabel,
  RECEIPT_REFERENCE_LABELS,
  RECEIPT_TYPE_LABELS,
  SALE_CONDITION_LABELS,
  SALE_PLATFORM_LABELS,
  SalesReceiptService
} from '../../../services/SalesReceipt/SalesReceiptService'

interface ISalesReceiptViewModalProps {
  id: number | null
  onClose: () => void
}

export default function SalesReceiptViewModal({ id, onClose }: ISalesReceiptViewModalProps) {
  const service = new SalesReceiptService()
  const toast = useAppToast()
  const [receipt, setReceipt] = useState<ISalesReceipt | null>(null)
  const [loading, setLoading] = useState(true)

  // Ajuste de estado durante a render, e não num efeito: o efeito só roda **depois da pintura**,
  // então na transição `null -> id` o primeiro quadro sairia com `loading=false` e com os dados do
  // registro anterior ainda na mão. O React refaz a render antes de pintar quando o `set` acontece
  // aqui, então o esqueleto aparece já na abertura. Fechar (`id -> null`) de propósito não mexe em
  // `loading`: é o que mantém o conteúdo na tela durante a animação de saída.
  const [openedId, setOpenedId] = useState(id)
  if (id !== openedId) {
    setOpenedId(id)
    if (id !== null) setLoading(true)
  }

  useEffect(() => {
    if (id === null) return

    const fetch = async () => {
      try {
        setReceipt(await service.get(id))
      } catch (e) {
        toast.error('Erro ao carregar comprovante', e)
        onClose()
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [id])

  // O valor ausente é do `DetailField` (um `-` em `gray.400`), não de cada `format*` daqui.
  const formatValue = (value: string | null) => (value === null ? null : formatCurrency(Number(value)))

  const formatPrize = (prize: number | null) => (!prize ? null : formatCurrency(prize))

  const buildRows = (data: ISalesReceipt): { label: string; value: ReactNode }[] => {
    const receiptLink = data.receipt_url && (
      <ChakraLink href={data.receipt_url} isExternal color="blue.500">
        Abrir comprovante
      </ChakraLink>
    )
    const typeRow = { label: 'Tipo', value: RECEIPT_TYPE_LABELS[data.receipt_type] }

    // Online (aulas gravadas) receipt — ABRA-162.
    if (data.sale_platform) {
      return [
        typeRow,
        { label: 'Consultor', value: data.consultant_name },
        { label: 'Nº da Fatura / Pedido', value: data.registration_number?.[0] },
        { label: 'E-mail do Aluno', value: data.student_email },
        { label: 'Curso(s)', value: data.courses?.map((c) => c.name).join(', ') },
        { label: 'Plataforma da Venda', value: SALE_PLATFORM_LABELS[data.sale_platform] ?? data.sale_platform },
        ...(data.sale_platform === 'internal_sale'
          ? [{ label: 'Forma de Pagamento', value: paymentMethodLabel(data.payment_method) }]
          : []),
        { label: 'Valor', value: formatValue(data.value) },
        { label: 'Data Envio', value: formatDateTime(data.created_at) },
        { label: 'Comprovante', value: receiptLink }
      ]
    }

    // Presencial receipt (enrollment or contract extension).
    return [
      typeRow,
      { label: 'Consultor', value: data.consultant_name },
      { label: 'Nº Matrícula', value: data.registration_number?.join(', ') },
      {
        label: 'Comprovante referente a',
        value: data.receipt_reference && RECEIPT_REFERENCE_LABELS[data.receipt_reference]
      },
      {
        label: 'Forma de Pagamento do Contrato',
        value: data.sale_condition && SALE_CONDITION_LABELS[data.sale_condition]
      },
      { label: 'Forma de Pagamento', value: paymentMethodLabel(data.payment_method) },
      ...(data.stone_id ? [{ label: 'Stone ID', value: data.stone_id }] : []),
      { label: 'Valor', value: formatValue(data.value) },
      { label: 'Prêmio', value: formatPrize(data.prize) },
      {
        label: 'Data de Pagamento do Prêmio',
        value: data.prize_payment_date && formatIsoDate(data.prize_payment_date)
      },
      { label: 'Data Envio', value: formatDateTime(data.created_at) },
      { label: 'Comprovante', value: receiptLink }
    ]
  }

  return (
    <FormModal isOpen={id !== null} onClose={onClose} title="Comprovante de Venda">
      {loading || !receipt ? <LoadingBlock /> : <DetailSheet fields={buildRows(receipt)} />}
    </FormModal>
  )
}
